/**
 * スマホの外注見積の詳細（閲覧のみ。#198、docs/79 §3.9・§13.3）。一覧の下からのシートと、詳細の URL をスマホで開いたときの画面が同じ中身を使う（§13.5 ヒ）。
 *   原本はページの画像（§13.2 決定 C。components/PageViewer の compact）と「別窓で原本表示」
 */
import { VENDOR_QUOTE_LIST_MSG, type VendorQuote, type VendorQuotePages } from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, messageOf } from "../../api.js";
import { PageViewer, type Zoom } from "../../components/PageViewer.jsx";
import { Sheet } from "../../components/Sheet.jsx";
import { ErrorText } from "../../components/ui.jsx";
import { useLabels } from "../../labels.js";
import { ReadCell, StateChip, docIcon, reasonText, receivedTitle, titleOf, yenOrDash } from "./parts.jsx";

/** 中身（シートと画面で同じ）。page＝画面に置く（シートでない） */
export function PhoneDetailBody({ id, page = false }: { id: string; page?: boolean }) {
  const L = useLabels();
  const q = useQuery({ queryKey: ["vendor-quote", id], queryFn: () => api<VendorQuote>("GET", `/vendor-quotes/${id}`), enabled: !!id });
  const [orig, setOrig] = useState(false);
  const [zoom, setZoom] = useState<Zoom>("fit");
  useEffect(() => setOrig(false), [id]);
  const pages = useQuery({ queryKey: ["vq-pages", id], queryFn: () => api<VendorQuotePages>("GET", `/vendor-quotes/${id}/pages`), enabled: !!id && orig, retry: false, gcTime: 0 });
  const vq = q.data;
  const row = vq?.row ?? null;
  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!vq || !row) return null;
  const why = reasonText(row);
  if (orig) {
    return (
      <div className={"vql-phorig" + (page ? " is-page" : "")}>
        <button type="button" className="btn text" onClick={() => setOrig(false)}>‹ 詳細</button>
        <div className="vql-phviewer">
          <PageViewer
            docKey={id}
            title={titleOf(row)}
            pages={pages.data?.pages}
            error={pages.isError ? pages.error : undefined}
            pageCount={vq.pageCount}
            zoom={zoom}
            onZoom={setZoom}
            compact
            label={VENDOR_QUOTE_LIST_MSG.original}
            ids={{ root: "vqd-ph-original", pages: "vqd-ph-pages", img: "vqd-ph-page-img", preparing: "vqd-ph-preparing" }}
          />
        </div>
        <a className="btn text" href={vq.original.url} target="_blank" rel="noreferrer">別窓で原本表示</a>
      </div>
    );
  }
  return (
    <div className="vql-phdetail">
      {page ? <h1 className="vqd-phname">{row.vendor?.name ?? `${L.vendor} ${VENDOR_QUOTE_LIST_MSG.unset}`}</h1> : null}
      <StateChip r={row} />
      {why ? <p className={row.state === "review" ? "shu" : "muted"} data-testid="vql-detail-why">{why}</p> : null}
      <dl>
        <dt>{L.project}</dt>
        <dd className={row.group ? undefined : "vql-unset"}>{row.group?.name ?? VENDOR_QUOTE_LIST_MSG.unset}</dd>
        <dt>件名</dt>
        <dd>{titleOf(row)}</dd>
        <dt>金額（税抜）</dt>
        <dd className="num">{yenOrDash(row.totalExTax)}</dd>
        <dt>消費税</dt>
        <dd className="num">{yenOrDash(row.taxAmount)}</dd>
        <dt>金額（税込）</dt>
        <dd className="num"><b>{yenOrDash(row.totalIncTax)}</b></dd>
        <dt>読み取り</dt>
        <dd><ReadCell r={row} /></dd>
        <dt>見積依頼</dt>
        <dd>{row.rfq ? `${row.rfq.displayNo}（${row.rfq.status === "answered" ? "回答あり" : "回答待ち"}）` : "—"}</dd>
        <dt>反映</dt>
        <dd className="num">{row.usage.all ? `${row.usage.done}／${row.usage.all}行` : "—"}</dd>
        <dt>担当者</dt>
        <dd>{row.assignee?.name ?? "—"}</dd>
        <dt>取込日</dt>
        <dd className="num">{receivedTitle(row.receivedAt)}</dd>
      </dl>
      <button type="button" className="btn vql-phorigbtn" onClick={() => setOrig(true)} data-testid="vql-detail-orig">{docIcon(row.source)}原本表示</button>
      {vq.lines.length ? (
        <>
          <h3>{VENDOR_QUOTE_LIST_MSG.readResult}<span className="muted">{row.usage.all}行</span></h3>
          <ul className="vql-phlines">
            {vq.lines.filter((l) => l.role !== "heading").map((l) => (
              <li key={l.id}>
                <span>{l.name}</span>
                <span className="num">{l.amount == null ? "" : l.amount.toLocaleString("ja-JP")}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <p className="muted vql-phnote">{VENDOR_QUOTE_LIST_MSG.phoneNote}</p>
    </div>
  );
}

/** 一覧の下からのシート（閲覧のみ） */
export function PhoneDetailSheet({ id, onClose }: { id: string | null; onClose: () => void }) {
  const L = useLabels();
  const q = useQuery({ queryKey: ["vendor-quote", id], queryFn: () => api<VendorQuote>("GET", `/vendor-quotes/${id}`), enabled: !!id });
  const row = q.data?.row ?? null;
  return (
    <Sheet open={!!id} title={row?.vendor?.name ?? (row ? `${L.vendor} ${VENDOR_QUOTE_LIST_MSG.unset}` : L.vendorQuote)} closeLabel="閉じる" onCancel={onClose} testId="vql-detail-sheet" className="vql-dsheet">
      {id ? <PhoneDetailBody id={id} /> : null}
    </Sheet>
  );
}
